import { useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { Building2, Mail, Phone, Globe, Check, X, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  new:      { background:"#fef3c7", color:"#92400e", border:"1px solid #fde68a" },
  approved: { background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" },
  rejected: { background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5" },
  reviewed: { background:"#dbeafe", color:"#1e40af", border:"1px solid #93c5fd" },
};

export default function AdminB2B() {
  const [data, setData] = useState({ items: [], pending: 0 });

  const load = async () => {
    const { data } = await adminApi.get("/admin/b2b-requests");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    await adminApi.put(`/admin/b2b-requests/${id}/status`, { status });
    toast.success(`Application ${status}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="admin-b2b-page">

      {/* Header */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Requests</div>
        <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>Trade / B2B Applications</h1>
        <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
          {data.pending} new application{data.pending !== 1 ? "s" : ""} awaiting review
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label:"Total",    value: data.items.length,                                       accent:"#1e5bb8" },
          { label:"Pending",  value: data.items.filter(b=>b.status==="new").length,           accent:"#d97706" },
          { label:"Approved", value: data.items.filter(b=>b.status==="approved").length,      accent:"#16a34a" },
        ].map(({ label, value, accent }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm"
            style={{ border:"1px solid #dde3ef", borderTop:`3px solid ${accent}` }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1" style={{ color:"#94a3b8" }}>{label}</div>
            <div className="font-heading text-[26px] font-bold" style={{ color:"#0f172a" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Applications */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden" style={{ border:"1px solid #dde3ef" }}>
        {data.items.length === 0 ? (
          <div className="p-14 text-center" data-testid="b2b-empty">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background:"#eff6ff" }}>
              <Building2 className="h-8 w-8" style={{ color:"#1e5bb8" }} />
            </div>
            <div className="font-heading font-bold text-[17px] mb-1" style={{ color:"#0f172a" }}>No applications yet</div>
            <div className="text-[13px]" style={{ color:"#64748b" }}>Trade account requests will appear here</div>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor:"#f0f4f8" }}>
            {data.items.map((b) => (
              <div key={b.id} className="p-5" data-testid={`b2b-row-${b.ref}`}>
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Company + status */}
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <div className="font-heading text-[17px] font-bold" style={{ color:"#0f172a" }}>{b.company}</div>
                      <span className="text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg"
                        style={STATUS_STYLE[b.status] || {}}>
                        {b.status}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color:"#94a3b8" }}>{b.ref}</span>
                    </div>

                    {/* Details grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[12px]">
                      <div style={{ color:"#475569" }}>Contact · <b style={{ color:"#0f172a" }}>{b.contact_name}</b></div>
                      <div className="flex items-center gap-1" style={{ color:"#475569" }}>
                        <Mail className="h-3 w-3 shrink-0"/> {b.email}
                      </div>
                      {b.phone && (
                        <div className="flex items-center gap-1" style={{ color:"#475569" }}>
                          <Phone className="h-3 w-3 shrink-0"/> {b.phone}
                        </div>
                      )}
                      {b.website && (
                        <div className="flex items-center gap-1" style={{ color:"#475569" }}>
                          <Globe className="h-3 w-3 shrink-0"/> {b.website}
                        </div>
                      )}
                      {b.abn && (
                        <div className="font-mono text-[11px]" style={{ color:"#475569" }}>
                          ABN · {b.abn}
                        </div>
                      )}
                      {b.business_type && (
                        <div style={{ color:"#475569" }}>Type · {b.business_type}</div>
                      )}
                      {b.monthly_volume && (
                        <div style={{ color:"#475569" }}>Est. volume · {b.monthly_volume}</div>
                      )}
                    </div>

                    {b.notes && (
                      <div className="mt-2 text-[12px] px-3 py-2 rounded-xl" style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
                        {b.notes}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  {b.status === "new" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => update(b.id, "approved")}
                        className="flex items-center gap-1.5 h-9 px-4 rounded-xl text-[12px] font-semibold transition-colors"
                        style={{ background:"#dcfce7", color:"#15803d", border:"1px solid #86efac" }}
                        data-testid={`b2b-approve-${b.ref}`}>
                        <Check className="h-3.5 w-3.5"/> Approve
                      </button>
                      <button onClick={() => update(b.id, "rejected")}
                        className="flex items-center gap-1.5 h-9 px-4 rounded-xl text-[12px] font-semibold transition-colors"
                        style={{ background:"#fee2e2", color:"#991b1b", border:"1px solid #fca5a5" }}
                        data-testid={`b2b-reject-${b.ref}`}>
                        <X className="h-3.5 w-3.5"/> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}